import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { FocusContainer } from '@eika/focus-container';

import useSelect from '../NettbankCreateTransactions/utils/useSelect';
import CustomSelectContext, { CustomSelectContextProps } from './CustomSelectContext';
import type { CustomSelectRootProps } from './types';

const CustomSelectRoot = ({
    id,
    className,
    onValueChange,
    value,
    focusContainerClassName,
    children,
    label,
    forceClose = false,
    ...rest
}: CustomSelectRootProps) => {
    const selectBoxRef = useRef<HTMLLabelElement | null>(null);
    const selectListRef = useRef<HTMLUListElement | null>(null);
    const selectButtonRef = useRef<HTMLButtonElement | null>(null);

    const {
        onKeyDownSelect,
        onKeyDownSelectItem,
        closeSelect,
        show: open,
        setShow: setOpen,
    } = useSelect(selectListRef, selectBoxRef as React.RefObject<HTMLDivElement>, selectButtonRef);

    useEffect(() => {
        setOpen(forceClose);
    }, [forceClose, setOpen]);

    const onKeyDownItem = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>, value: string) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                onValueChange(value);
            }
            if (e.key === 'Esc' || e.key === 'Escape') {
                if (open) {
                    e.stopPropagation();
                }
                closeSelect();
                return;
            }

            onKeyDownSelectItem(e);
        },
        [open, onValueChange, onKeyDownSelectItem, closeSelect],
    );

    const context: CustomSelectContextProps = useMemo(
        () => ({
            id,
            open,
            onClose: closeSelect,
            value,
            onValueChange,
            onKeyDownSelect,
            setButtonRef: (element: HTMLButtonElement) => {
                selectButtonRef.current = element;
            },
            setListRef: (element: HTMLUListElement) => {
                selectListRef.current = element;
            },
            onKeyDownItem,
            toggleOpen: () => setOpen(!open),
        }),
        [
            setOpen,
            value,
            onValueChange,
            onKeyDownSelect,
            selectButtonRef,
            selectListRef,
            id,
            onKeyDownItem,
            open,
            closeSelect,
        ],
    );

    return (
        <CustomSelectContext.Provider value={context}>
            <label
                {...rest}
                htmlFor={id}
                className={classNames('custom-select', className, {
                    'custom-select--open': open,
                })}
                ref={selectBoxRef}
            >
                {label && <span className="custom-select__label semibold">{label}</span>}
                <div
                    className={classNames('custom-select__focus-wrapper', {
                        'custom-select__focus-wrapper--open': open,
                    })}
                >
                    <FocusContainer
                        entry={open}
                        onExit={() => setOpen(false)}
                        containerClassName={classNames('custom-select__focus-container', focusContainerClassName)}
                    >
                        {children}
                    </FocusContainer>
                </div>
            </label>
        </CustomSelectContext.Provider>
    );
};

export default CustomSelectRoot;
